import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useBarcodeSession } from '../contexts/BarcodeSessionContext';

/**
 * Check if a barcode exists in the database
 * @param {Object} okapi - Okapi object with URL, tenant, and token
 * @param {string} barcode - Barcode to check
 * @returns {Promise<boolean>} True if barcode exists in database
 */
const checkBarcodeInDatabase = async (okapi, barcode) => {
  const response = await fetch(`${okapi.url}/inventory/items?query=(barcode=="${barcode}")`, {
    headers: {
      'X-Okapi-Tenant': okapi.tenant,
      'Content-Type': 'application/json',
      ...(okapi.token && { 'X-Okapi-Token': okapi.token }),
    },
    credentials: 'include',
  });

  if (response.status >= 400) {
    throw new Error(`Database check failed with status ${response.status}`);
  }

  const json = await response.json();
  return json.totalRecords > 0;
};

/**
 * Validate a single chunk of barcodes against the database
 * @param {Object} okapi - Okapi object with URL, tenant, and token
 * @param {string[]} barcodes - Array of barcodes to check (should be ≤ BATCH_SIZE)
 * @returns {Promise<Object>} Object with barcode as key and boolean existence as value
 */
const validateSingleChunk = async (okapi, barcodes) => {
  if (!barcodes || barcodes.length === 0) {
    return {};
  }

  // Create a query to check all barcodes in this chunk
  const query = barcodes.map(barcode => `barcode=="${barcode}"`).join(' or ');
  const response = await fetch(`${okapi.url}/inventory/items?query=(${query})&limit=${barcodes.length}`, {
    headers: {
      'X-Okapi-Tenant': okapi.tenant,
      'Content-Type': 'application/json',
      ...(okapi.token && { 'X-Okapi-Token': okapi.token }),
    },
    credentials: 'include',
  });

  if (response.status >= 400) {
    throw new Error(`Database chunk validation failed with status ${response.status}`);
  }

  const json = await response.json();
  const existingBarcodes = json.items?.map(item => item.barcode) || [];

  // Create result object mapping each barcode to its existence status
  const result = {};
  barcodes.forEach(barcode => {
    result[barcode] = existingBarcodes.includes(barcode);
  });

  return result;
};

/**
 * Batch validate multiple barcodes against the database using chunking to avoid 414 errors
 * @param {Object} okapi - Okapi object with URL, tenant, and token
 * @param {string[]} barcodes - Array of barcodes to check
 * @returns {Promise<Object>} Object with barcode as key and boolean existence as value
 */
const validateBarcodesBatch = async (okapi, barcodes) => {
  if (!barcodes || barcodes.length === 0) {
    return {};
  }

  // Configuration for batch processing
  const BATCH_SIZE = 50; // Maximum barcodes per request to avoid 414 errors
  const MAX_CONCURRENT = 5; // Maximum concurrent requests

  // Split barcodes into chunks
  const chunks = [];
  for (let i = 0; i < barcodes.length; i += BATCH_SIZE) {
    chunks.push(barcodes.slice(i, i + BATCH_SIZE));
  }

  // Process chunks in batches to avoid overwhelming the server
  const results = {};
  for (let i = 0; i < chunks.length; i += MAX_CONCURRENT) {
    const currentBatch = chunks.slice(i, i + MAX_CONCURRENT);
    
    try {
      // Process current batch of chunks in parallel
      const batchResults = await Promise.all(
        currentBatch.map(chunk => validateSingleChunk(okapi, chunk))
      );
      
      // Merge results from current batch
      batchResults.forEach(chunkResult => {
        Object.assign(results, chunkResult);
      });
    } catch (error) {
      console.error(`Error processing batch starting at chunk ${i}:`, error);
      // Continue with remaining batches even if one fails
    }
  }

  return results;
};

/**
 * Hook for comprehensive barcode validation including session-level duplicate checking
 * @returns {Object} Validation functions and utilities
 */
const useBarcodeValidation = () => {
  const intl = useIntl();
  const { checkBarcodeExists, validateBarcodeRange, generateSequentialBarcode } = useBarcodeSession();

  /**
   * Default validation messages
   */
  const getValidationMessages = useCallback(() => ({
    empty: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.empty',
      defaultMessage: 'Barcode cannot be empty',
    }),
    duplicate: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.duplicate',
      defaultMessage: 'This barcode already exists in the current session',
    }),
    sessionDuplicate: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.sessionDuplicate',
      defaultMessage: 'This barcode conflicts with another item in the current session',
    }),
    invalidCharacters: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.invalidCharacters',
      defaultMessage: 'Barcode contains invalid characters',
    }),
    tooShort: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.tooShort',
      defaultMessage: 'Barcode must be at least 3 characters long',
    }),
    valid: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.valid',
      defaultMessage: 'Barcode is valid and available',
    }),
    error: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.error',
      defaultMessage: 'Error validating barcode',
    }),
    rangeConflicts: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.rangeConflicts',
      defaultMessage: 'Some barcodes in this range already exist in the current session',
    }),
    dbDuplicate: intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.dbDuplicate',
      defaultMessage: 'This barcode already exists in the database',
    }),
  }), [intl]);

  /**
   * Validate a single barcode
   * @param {string} barcode - Barcode to validate
   * @param {Object} options - Validation options
   * @param {string[]} options.existingBarcodes - Additional barcodes to check against (e.g., from form)
   * @param {Function} options.customValidation - Custom validation function
   * @param {Object} options.okapi - Okapi object for database validation
   * @returns {Promise<Object>} Validation result with isValid, message, and type
   */
  const validateSingleBarcode = useCallback(async (barcode, options = {}) => {
    const { existingBarcodes = [], customValidation, okapi } = options;
    const messages = getValidationMessages();

    // Custom validation first (if provided)
    if (customValidation && typeof customValidation === 'function') {
      try {
        const customResult = await customValidation(barcode);
        if (customResult && typeof customResult === 'object') {
          return customResult;
        }
      } catch (error) {
        console.error('Custom validation error:', error);
      }
    }

    // Basic validation
    if (!barcode || typeof barcode !== 'string' || barcode.trim() === '') {
      return {
        isValid: false,
        message: messages.empty,
        type: 'EMPTY',
      };
    }

    const trimmedBarcode = barcode.trim();

    // Format validation - alphanumeric plus hyphens and underscores
    const barcodeRegex = /^[A-Za-z0-9-_]+$/;
    if (!barcodeRegex.test(trimmedBarcode)) {
      return {
        isValid: false,
        message: messages.invalidCharacters,
        type: 'INVALID_FORMAT',
      };
    }

    // Length validation
    if (trimmedBarcode.length < 3) {
      return {
        isValid: false,
        message: messages.tooShort,
        type: 'TOO_SHORT',
      };
    }

    // Check against existing barcodes from form/props
    if (existingBarcodes && existingBarcodes.includes(trimmedBarcode)) {
      return {
        isValid: false,
        message: messages.duplicate,
        type: 'FORM_DUPLICATE',
      };
    }

    // Session duplicate check
    if (checkBarcodeExists(trimmedBarcode)) {
      return {
        isValid: false,
        message: messages.sessionDuplicate,
        type: 'SESSION_DUPLICATE',
      };
    }

    // Database duplicate check if okapi is provided
    if (okapi) {
      try {
        const dbExists = await checkBarcodeInDatabase(okapi, trimmedBarcode);
        if (dbExists) {
          return {
            isValid: false,
            message: messages.dbDuplicate,
            type: 'DB_DUPLICATE',
          };
        }
      } catch (error) {
        console.warn('Database barcode validation error:', error);
        // Continue with other validation if database check fails
      }
    }

    return {
      isValid: true,
      message: messages.valid,
      type: 'VALID',
    };
  }, [checkBarcodeExists, getValidationMessages]);

  /**
   * Validate a sequence of barcodes using comprehensive validation
   * @param {string} startingBarcode - Starting barcode pattern
   * @param {number} count - Number of barcodes to generate and validate
   * @param {Object} options - Validation options
   * @param {string[]} options.existingBarcodes - Additional barcodes to check against
   * @param {Function} options.customValidation - Custom validation function
   * @param {Object} options.okapi - Okapi object for database validation
   * @returns {Promise<Object>} Validation result with conflicts array
   */
  const validateBarcodeSequence = useCallback(async (startingBarcode, count, options = {}) => {
    const { existingBarcodes = [], customValidation, okapi } = options;
    const messages = getValidationMessages();

    if (!startingBarcode || typeof startingBarcode !== 'string' || count <= 0) {
      return {
        isValid: false,
        message: messages.empty,
        type: 'INVALID_INPUT',
        conflicts: [],
      };
    }

    const validCount = Math.max(1, parseInt(count, 10) || 1);
    const allConflicts = [];

    // Generate all barcodes first
    const generatedBarcodes = [];
    for (let i = 0; i < validCount; i++) {
      const barcode = generateSequentialBarcode(startingBarcode, i);
      generatedBarcodes.push(barcode);
    }

    // Batch database validation if okapi is provided
    let dbExistenceMap = {};
    if (okapi) {
      try {
        const validBarcodes = generatedBarcodes.filter(Boolean);
        if (validBarcodes.length > 0) {
          console.log(`Starting database validation for ${validBarcodes.length} barcodes...`);
          dbExistenceMap = await validateBarcodesBatch(okapi, validBarcodes);
          console.log(`Database validation completed. Found ${Object.values(dbExistenceMap).filter(Boolean).length} existing barcodes.`);
        }
      } catch (error) {
        console.warn('Database batch validation error:', error);
        // Continue with other validation if database check fails
      }
    }

    // Validate each barcode with proper order: format/length first, then conflicts
    for (let i = 0; i < validCount; i++) {
      const barcode = generatedBarcodes[i];

      if (barcode) {
        const trimmedBarcode = barcode.trim();

        // 1. First: Basic format and length validation
        const barcodeRegex = /^[A-Za-z0-9-_]+$/;
        if (!barcodeRegex.test(trimmedBarcode)) {
          allConflicts.push({
            index: i,
            barcode,
            position: i + 1,
            type: 'INVALID_FORMAT',
            message: messages.invalidCharacters,
          });
          continue; // Skip conflict checks if format is invalid
        }

        if (trimmedBarcode.length < 3) {
          allConflicts.push({
            index: i,
            barcode,
            position: i + 1,
            type: 'TOO_SHORT',
            message: messages.tooShort,
          });
          continue; // Skip conflict checks if too short
        }

        // 2. Then: Custom validation (if provided)
        if (customValidation && typeof customValidation === 'function') {
          try {
            const customResult = await customValidation(barcode);
            if (customResult && typeof customResult === 'object' && !customResult.isValid) {
              allConflicts.push({
                index: i,
                barcode,
                position: i + 1,
                type: customResult.type || 'CUSTOM_ERROR',
                message: customResult.message,
              });
              continue; // Skip other checks if custom validation fails
            }
          } catch (error) {
            console.error('Custom validation error:', error);
          }
        }

        // 3. Finally: Conflict validation (form, session, database)
        let hasConflict = false;

        // Check against existing barcodes from form/props
        if (existingBarcodes && existingBarcodes.includes(trimmedBarcode)) {
          allConflicts.push({
            index: i,
            barcode,
            position: i + 1,
            type: 'FORM_DUPLICATE',
            message: messages.duplicate,
          });
          hasConflict = true;
        }

        // Check session duplicates (only if no form conflict)
        if (!hasConflict && checkBarcodeExists(trimmedBarcode)) {
          allConflicts.push({
            index: i,
            barcode,
            position: i + 1,
            type: 'SESSION_DUPLICATE',
            message: messages.sessionDuplicate,
          });
          hasConflict = true;
        }

        // Check database conflicts (only if no other conflicts)
        if (!hasConflict && dbExistenceMap[barcode]) {
          allConflicts.push({
            index: i,
            barcode,
            position: i + 1,
            type: 'DB_DUPLICATE',
            message: messages.dbDuplicate,
          });
        }
      } else {
        // If barcode generation failed
        allConflicts.push({
          index: i,
          barcode: '',
          position: i + 1,
          type: 'GENERATION_ERROR',
          message: 'Failed to generate barcode for this position',
        });
      }
    }

    // Return results based on conflicts found
    if (allConflicts.length > 0) {
      const conflictCount = allConflicts.length;

      // Group conflicts by type for better messaging
      const conflictTypes = allConflicts.reduce((acc, conflict) => {
        acc[conflict.type] = (acc[conflict.type] || 0) + 1;
        return acc;
      }, {});

      // Create message based on primary issue type (format/length issues have priority over conflicts)
      let message;
      if (conflictTypes.INVALID_FORMAT) {
        message = messages.invalidCharacters;
      } else if (conflictTypes.TOO_SHORT) {
        message = messages.tooShort;
      } else if (conflictTypes.GENERATION_ERROR) {
        message = messages.error;
      } else if (conflictTypes.SESSION_DUPLICATE || conflictTypes.FORM_DUPLICATE || conflictTypes.DB_DUPLICATE) {
        message = intl.formatMessage({
          id: 'ui-plugin-inventory-templates.barcode.validation.rangeConflictsCount',
          defaultMessage: '{count, plural, one {# barcode} other {# barcodes}} in this range already exist',
        }, { count: conflictCount });
      } else {
        message = messages.error;
      }

      return {
        isValid: false,
        message,
        type: 'RANGE_CONFLICTS',
        conflicts: allConflicts,
        conflictCount,
      };
    }

    // All barcodes are valid
    const message = intl.formatMessage({
      id: 'ui-plugin-inventory-templates.barcode.validation.rangeValid',
      defaultMessage: 'All {count} barcodes in this range are valid and available',
    }, { count: validCount });

    return {
      isValid: true,
      message,
      type: 'RANGE_VALID',
      conflicts: [],
      conflictCount: 0,
    };
  }, [validateSingleBarcode, generateSequentialBarcode, getValidationMessages, intl]);

  /**
   * Generate preview of barcode sequence
   * @param {string} startingBarcode - Starting barcode pattern
   * @param {number} count - Number of barcodes to preview
   * @param {number} maxPreview - Maximum number of barcodes to show in preview
   * @returns {Object} Preview data with barcodes array and hasMore flag
   */
  const generateBarcodePreview = useCallback((startingBarcode, count, maxPreview = 5) => {
    if (!startingBarcode || count <= 0) {
      return { barcodes: [], hasMore: false, total: 0 };
    }

    const validCount = Math.max(1, parseInt(count, 10) || 1);
    const previewCount = Math.min(validCount, maxPreview);
    const barcodes = [];

    for (let i = 0; i < previewCount; i++) {
      const barcode = generateSequentialBarcode(startingBarcode, i);
      if (barcode) {
        barcodes.push({
          index: i,
          barcode,
          position: i + 1,
        });
      }
    }

    return {
      barcodes,
      hasMore: validCount > previewCount,
      remaining: validCount - previewCount,
      total: validCount,
    };
  }, [generateSequentialBarcode]);

  return {
    validateSingleBarcode,
    validateBarcodeSequence,
    generateBarcodePreview,
    getValidationMessages,
    validateBarcodesBatch: useCallback((okapi, barcodes) => validateBarcodesBatch(okapi, barcodes), []),
    checkBarcodeInDatabase: useCallback((okapi, barcode) => checkBarcodeInDatabase(okapi, barcode), []),
  };
};

export default useBarcodeValidation;
